import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Power, RefreshCw, Send, Trash2, X } from "lucide-react";
import { WEBHOOK_EVENTS, type WebhookDeliveryDto, type WebhookDto, type WebhookWithSecret } from "@ambulance/shared";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import { btnPrimary, btnSecondary, ErrorBox, Field, Forbidden, inputCls, Modal, Spinner } from "../components/ui";

const statusCls: Record<string, string> = { delivered: "bg-emerald-100 text-emerald-800", failed: "bg-red-100 text-red-800", pending: "bg-amber-100 text-amber-800" };

function Deliveries({ hook, onClose }: { hook: WebhookDto; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["webhook-deliveries", hook.id], queryFn: () => api.get<WebhookDeliveryDto[]>(`/api/v1/webhooks/${hook.id}/deliveries?limit=100`), refetchInterval: 5000 });
  const redeliver = useMutation({ mutationFn: (deliveryId: string) => api.post<void>(`/api/v1/webhooks/${hook.id}/deliveries/${deliveryId}/redeliver`), onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook-deliveries", hook.id] }) });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Modal title="Deliveries" subtitle={hook.url} onClose={onClose} size="lg">
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox>{q.error.message}</ErrorBox>
      ) : q.data.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing delivered yet. Use "Send test" to try the endpoint.</p>
      ) : (
        <div className="space-y-2 text-sm">
          {q.data.map((d) => (
            <div key={d.id} className="border border-slate-200 rounded-lg p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${statusCls[d.status]}`}>{d.status}</span>
                <span className="font-mono text-xs">{d.event}</span>
                <span className="text-slate-500 text-xs">{new Date(d.createdAt).toLocaleString()}</span>
                <span className="text-slate-500 text-xs">attempts {d.attempts}</span>
                {d.lastError && <span className="text-red-700 text-xs">{d.lastError}</span>}
                <span className="flex-1" />
                <button type="button" className="text-xs text-slate-600 hover:underline" onClick={() => setOpen(open === d.id ? null : d.id)}>{open === d.id ? "Hide payload" : "Payload"}</button>
                <button type="button" className="text-xs text-[var(--hmg-red)] hover:underline inline-flex items-center gap-1" onClick={() => redeliver.mutate(d.id)}><RefreshCw className="w-3 h-3" /> Resend</button>
              </div>
              {open === d.id && <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 text-xs overflow-x-auto">{JSON.stringify(d.payload, null, 2)}</pre>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** Super admin: outbound webhook subscriptions and their delivery history. */
export function WebhooksPage() {
  const { me, isLoading } = useMe();
  const api = useApi();
  const qc = useQueryClient();
  const hooks = useQuery({ queryKey: ["webhooks"], queryFn: () => api.get<WebhookDto[]>("/api/v1/webhooks"), enabled: Boolean(api.token), refetchInterval: 10_000 });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["webhooks"] });

  const [showCreate, setShowCreate] = useState(false);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>(["checklist.created"]);
  const [secretFor, setSecretFor] = useState<WebhookWithSecret | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<WebhookDto | null>(null);

  const create = useMutation({ mutationFn: () => api.post<WebhookWithSecret>("/api/v1/webhooks", { url, events }), onSuccess: (w) => { invalidate(); setShowCreate(false); setUrl(""); setSecretFor(w); } });
  const toggle = useMutation({ mutationFn: (w: WebhookDto) => api.patch<WebhookDto>(`/api/v1/webhooks/${w.id}`, { active: !w.active }), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.del<void>(`/api/v1/webhooks/${id}`), onSuccess: invalidate });
  const test = useMutation({ mutationFn: (id: string) => api.post<{ deliveryId: string }>(`/api/v1/webhooks/${id}/test`), onSuccess: invalidate });

  if (isLoading) return <Spinner />;
  if (!me || me.role !== "super_admin") return <Forbidden />;
  const list = hooks.data ?? [];
  const toggleEvent = (e: string) => setEvents((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Webhooks</h1>
        <button type="button" onClick={() => setShowCreate(true)} className={btnPrimary}><Plus className="w-4 h-4" /> Add endpoint</button>
      </div>
      <p className="text-sm text-slate-600 mb-6">The platform POSTs a JSON body to your endpoint whenever a subscribed event happens. Each request carries an HMAC-SHA256 signature so the receiver can prove the source; failed deliveries are retried with backoff for about four hours. See the Developers page for the verification recipe.</p>

      {hooks.isPending ? (
        <Spinner />
      ) : hooks.isError ? (
        <ErrorBox>{hooks.error.message}</ErrorBox>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">No webhook endpoints yet.</div>
      ) : (
        <div className="space-y-3">
          {list.map((w) => (
            <div key={w.id} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${w.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{w.active ? "Active" : "Paused"}</span>
                <span className="font-mono text-sm break-all">{w.url}</span>
                <span className="flex-1" />
                <button type="button" className={btnSecondary} onClick={() => test.mutate(w.id)} disabled={test.isPending}><Send className="w-4 h-4" /> Send test</button>
                <button type="button" className={btnSecondary} onClick={() => setDeliveriesFor(w)}>Deliveries</button>
                <button type="button" title={w.active ? "Pause" : "Resume"} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" onClick={() => toggle.mutate(w)}><Power className="w-4 h-4" /></button>
                <button type="button" title="Delete" className="p-1.5 rounded hover:bg-red-50 text-red-600" onClick={() => { if (confirm("Delete this endpoint and its delivery history?")) remove.mutate(w.id); }}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {w.events.map((e) => <span key={e} className="text-xs font-mono bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">{e}</span>)}
              </div>
              <div className="mt-2 text-xs text-slate-500">Last 100 deliveries: {w.recent.delivered} delivered · {w.recent.pending} pending · {w.recent.failed} failed</div>
            </div>
          ))}
        </div>
      )}
      {(toggle.isError || remove.isError || test.isError) && <div className="mt-3"><ErrorBox>{(toggle.error ?? remove.error ?? test.error)?.message}</ErrorBox></div>}

      {showCreate && (
        <Modal title="Add webhook endpoint" onClose={() => setShowCreate(false)} size="lg">
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <Field label="Endpoint URL (https)"><input className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-system.example/hooks/ambulance" required /></Field>
            <div>
              <div className="text-sm font-medium text-slate-700 mb-1">Events</div>
              <label className="flex items-center gap-2 text-sm mb-1"><input type="checkbox" checked={events.includes("*")} onChange={() => setEvents(events.includes("*") ? [] : ["*"])} /> <span className="font-mono">*</span> <span className="text-slate-500">all events</span></label>
              <div className="grid grid-cols-2 gap-1 max-h-56 overflow-y-auto border border-slate-200 rounded p-2">
                {WEBHOOK_EVENTS.map((e) => (
                  <label key={e} className={`flex items-center gap-2 text-sm ${events.includes("*") ? "opacity-40" : ""}`}><input type="checkbox" disabled={events.includes("*")} checked={events.includes(e)} onChange={() => toggleEvent(e)} /> <span className="font-mono text-xs">{e}</span></label>
                ))}
              </div>
            </div>
            {create.isError && <ErrorBox>{create.error.message}</ErrorBox>}
            <div className="flex justify-end gap-2">
              <button type="button" className={btnSecondary} onClick={() => setShowCreate(false)}>Cancel</button>
              <button type="submit" className={btnPrimary} disabled={create.isPending || events.length === 0}>{create.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
            </div>
          </form>
        </Modal>
      )}

      {secretFor && (
        <Modal title="Signing secret" subtitle="Store it in your system now. It is shown only once." onClose={() => setSecretFor(null)} size="lg">
          <div className="space-y-3 text-sm">
            <div className="font-mono bg-slate-50 border border-slate-200 rounded p-2 break-all">{secretFor.secret}</div>
            <p className="text-slate-600">Each delivery carries <span className="font-mono">X-Ambulance-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</span> where v1 = HMAC-SHA256(secret, <span className="font-mono">t + "." + rawBody</span>). Reject requests whose timestamp is older than five minutes.</p>
            <div className="flex justify-end"><button type="button" className={btnPrimary} onClick={() => setSecretFor(null)}><X className="w-4 h-4" /> Close</button></div>
          </div>
        </Modal>
      )}

      {deliveriesFor && <Deliveries hook={deliveriesFor} onClose={() => setDeliveriesFor(null)} />}
    </div>
  );
}
